import StackTrace from "stacktrace-js";

import { logClientMessage } from "../api/TdsApi";
import * as constants from "../constants/constants";

export function setState(state) {
  switch (state) {
    case 0:
      return "In Progress";
    case 1:
      return "Ready";
    case 2:
      return "Failed";
    case 3:
      return "Paused";
    default:
      return "Unknown";
  }
}

export function setTopologyStateTag(state) {
  switch (state) {
    case 0:
      return "topopending";
    case 1:
      return "topoready";
    case 2:
      return "topofailed";
    case 3:
      return "topopaused";
    default:
      return "Unknown";
  }
}

export function setTaskState(state) {
  switch (state) {
    case 0:
      return "Pending";
    case 1:
      return "Succeeded";
    case 2:
      return "Failed";
    case 3:
      return "Skipped";
    case 4:
      return "Running";
    case 5:
      return "Cancelled";
    case 6:
      return "Paused";

    default:
      return "Unknown";
  }
}

export function setTaskStateTag(state) {
  switch (state) {
    case 0:
      return "taskpending";
    case 1:
      return "tasksucceeded";
    case 2:
      return "taskfailed";
    case 3:
      return "taskskipped";
    case 4:
      return "taskrunning";
    case 5:
      return "taskcancelled";
    case 6:
      return "taskpaused";

    default:
      return "Unknown";
  }
}

export function setLogLevel(state) {
  switch (state) {
    case 0:
      return "Information";
    case 1:
      return "Warning";
    case 2:
      return "Error";

    default:
      return "Unknown";
  }
}

// Translate RecoverTime to days to expiry
export function computeDaysToExpiry(recoverTime) {
  // Make ISO string into date, get date, convert to UTC
  let expireDate = new Date(recoverTime);
  let nowDate = new Date();

  let utcExpire = Date.UTC(expireDate.getFullYear(), expireDate.getMonth(), expireDate.getDate());
  let utcNow = Date.UTC(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate());

  // 1000 milliseconds, 60 seconds, 60 minutes, 24 hours
  let milliSecondsPerDay = 1000 * 60 * 60 * 24;

  let daysToExpiry = Math.floor((utcExpire - utcNow) / milliSecondsPerDay);
  if (daysToExpiry < 0) {
    daysToExpiry = 0;
  }

  // Use 'days' for plural and 'day' for singular
  let day = daysToExpiry === 1 ? " day" : " days";

  return daysToExpiry + day;
}

export function getCurrentDateFormatted() {
  let date = new Date();
  let formattedDate =
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds());

  return formattedDate;
}

function pad(num) {
  let r = String(num);
  if (r.length === 1) {
    r = "0" + r;
  }
  return r;
}

export function customNotSupported(deploymentLookupFileName) {
  return constants.CUSTOM_NOT_SUPPORTED.filter((c) => c === deploymentLookupFileName).length > 0;
}

export function logMessage(logLevel, messageToLog, apiUri, environment) {
  try {
    const callback = (stackframes) => {
      let stackTrace = stackframes
        .map((sf) => {
          return sf.toString();
        })
        .join("\n");

      processMessage(logLevel, messageToLog, stackTrace, apiUri, environment);
    };

    const errorCallback = (errorMsg) => {
      console.error(errorMsg);
    };

    StackTrace.get().then(callback).catch(errorCallback);
  } catch (loggingError) {
    console.error("Server-side logging failed");
  }
}

export function processMessage(logLevel, messageToLog, stackTrace, apiUri, environment) {
  let message = logLevel + ": " + messageToLog;

  // Handle log level type
  switch (logLevel) {
    // Debug and error will go to server
    case constants.LOG_LEVEL.DEBUG:
      message += "\nURL: " + window.location.href;
      console.log(message);
      break;
    case constants.LOG_LEVEL.ERROR:
      if (apiUri) {
        message += "\nApi URI: " + apiUri;
      } else {
        message += "\nCurrent URL: " + window.location.href;
      }

      message += "\nStack: " + stackTrace;
      console.error(message);

      // Don't try to call log to server if this is a token issue
      if (message.indexOf(constants.TOKEN_ERROR_MESSAGE) > -1) {
        return;
      }

      break;
    case constants.LOG_LEVEL.CONSOLE_ONLY:
      console.log(message);
      return;

    default:
      // Do nothing
      console.error("Invalid log level provided.");
      return;
  }

  // Async, but don't care when it returns
  logClientMessage(message, environment);
}

/**
 * Determines wheter we are using an Azure environment
 * @param environment the environment type
 * @returns true if we are using an Azure environment
 */
export const isAzureEnvironment = (environment) => environment.environmentType === "Cloud";

export const isCloudEnvironment = isAzureEnvironment;

export function isTdsa(environment) {
  if (!environment) {
    return false;
  }

  if (!process.env.REACT_APP_STACKA_NAME) {
    return false;
  }

  return environment.name.indexOf(process.env.REACT_APP_STACKA_NAME) > -1;
}

export function latestOSFirst(templates) {
  // eslint-disable-next-line
  var latest = templates.filter(function (entry) {
    for (var i = 0; i < constants.LATEST_OS.length; i++) {
      if (entry.DisplayName.indexOf(constants.LATEST_OS[i]) > -1) {
        return entry;
      }
    }
  });

  var other = templates.filter(function (entry) {
    return latest.indexOf(entry) === -1;
  });

  return latest.concat(other);
}

export function findIndex(nameToFind, arrayToSearch) {
  return arrayToSearch.findIndex((entry) => entry.Name.indexOf(nameToFind) > -1);
}

export function buildTypeOptions() {
  let options = [];

  constants.BUILD_TYPES.forEach((b) => {
    options.push({ key: b, text: b });
  });

  return options;
}

export function getTopologyTypesByEnvironmentType(topologyTypes, environments, cloudType) {
  let envs = cloudType
    ? environments.filter((item) => isAzureEnvironment(item))
    : environments.filter((item) => !isAzureEnvironment(item));

  let returnTypes = [];

  for (var i = 0; i < envs.length; i++) {
    let env = envs[i];

    let types = topologyTypes.filter((item) => item.environment.name === env.name);
    for (var j = 0; j < types.length; j++) {
      returnTypes.push(types[j]);
    }
  }

  return returnTypes;
}

export function replaceClassicWithCloudTypes(cloudTypes, classicTypes, classicMatched) {
  let types = [];

  for (var i = 0; i < classicTypes.length; i++) {
    let found = false;
    for (var j = 0; j < cloudTypes.length; j++) {
      if (classicTypes[i].DisplayName === cloudTypes[j].DisplayName) {
        found = true;
        classicMatched.push(classicTypes[i]); // If classic match is found, add to classicMatched
        break;
      }
    }
    if (!found) {
      types.push(classicTypes[i]);
    }
  }

  return cloudTypes.concat(types);
}

export function filterCloudTypes(cloudTypes, typesToFilter) {
  let returnTypes = cloudTypes;
  if (typesToFilter && typesToFilter.length > 0) {
    returnTypes = cloudTypes.filter((item) => typesToFilter.indexOf(item.Id) < 0);
  }

  return returnTypes;
}

export function setQueueRequest(state) {
  switch (state) {
    case 1:
      return "Requested";
    case 2:
      return "Completed";
    case 3:
      return "Error";
    case 4:
      return "Cancelled by owner";
    case 5:
      return "Cancelled by system";

    default:
      return "Unknown";
  }
}

export const cookieManager = {
  /**
   * Sets a cookie with the specified name and value.
   * @param {string} name - The name of the cookie.
   * @param {string} value - The value of the cookie.
   */
  setCookie: function (name, value) {
    document.cookie = name + "=" + encodeURIComponent(value) + ";path=/";
  },

  /**
   * Gets the value of the cookie with the specified name.
   * @param {string} name - The name of the cookie to retrieve.
   * @returns {string|null} The value of the cookie, or null if the cookie does not exist.
   */
  getCookie: function (name) {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(name + "=")) {
        return decodeURIComponent(cookie.substring(name.length + 1));
      }
    }
    return null;
  },
};
